import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  CloseIcon,
  DeleteIcon,
  DragIcon,
  ImageIcon,
  InfoIcon,
  SettingsIcon,
  SquareIcon,
  StarFilledIcon,
} from '@sketchnote/icons';
import { Button, FileUpload, Switch } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import { parseISO } from 'date-fns';
import isNil from 'lodash/isNil';
import { forwardRef, Fragment, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
} from 'react-beautiful-dnd';
import { UploadImage } from 'src/components/UploadImage';
import { DateRangePicker } from 'src/features/kanban/component/Item/DatePicker';
import { v4 as uuid } from 'uuid';

import { FileUploadType, FileUploadTypes, Form, FormField, FormOption } from '../../../types/form';
import { SelectOption } from '../../SelectOption';
import { options, styleoption } from './field-labels';

export interface FieldProps {
  onAddNewField: () => void;
  onDeleteField: () => void;
  data: FormField;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onTypeChange: (type: FormField['responseType']) => void;
  onRequiredChange: (required: boolean) => void;
  onPlaceholderChange: (placeholder: string) => void;
  onChangeRange?: (value, type: 'lowest' | 'highest') => void;
  onChangeRatingStyle?: (value: 'star' | 'heart' | 'smile') => void;
  onChangeRangeLabels?: (value: string, type: 'lowest' | 'highest') => void;
  onAddOptions?: (options: any) => void;
  onAddImage?: (image: string | undefined) => void;
  onRandomizeOptions?: (randomize: boolean) => void;
  onDeleteOption: (option: FormOption) => void;
  onReorderFormFieldOptions?: (options: any) => void;
  onChangeDateRange?: (dateRange: [Date, Date] | [Date] | null) => void;
  onChangeMaxFiles: (count: number) => void;
  onChangeMaxFileSize: (size: number) => void;
  onChangeFileTypes: (types: FileUploadType[]) => void;
  formData: Form;
  index: number;
  dragHandleProps: DraggableProvidedDragHandleProps | undefined;
}

export const Field = ({
  onAddNewField,
  onDeleteField,
  data,
  onTitleChange,
  onDescriptionChange,
  onTypeChange,
  onRequiredChange,
  onPlaceholderChange,
  onChangeRatingStyle,
  onChangeRange,
  onChangeRangeLabels,
  onAddOptions,
  onAddImage,
  onRandomizeOptions,
  onDeleteOption,
  onReorderFormFieldOptions,
  onChangeDateRange,
  onChangeMaxFiles,
  onChangeMaxFileSize,
  onChangeFileTypes,
  formData,
  index,
  dragHandleProps,
}: FieldProps) => {
  const timerEnabled = !!formData?.timer;
  const isFormToTaskSelectedField = formData.formToTaskMap?.field === data._id;
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [imageEditOption, setImageEditOption] = useState('');

  const [currentURL, setCurrentURL] = useState<string>('');
  const [hoveredOptionIndex, setHoveredOptionIndex] = useState<number>();
  const [viewImage, setViewImage] = useState<any>('');
  const handleMenuOpen = () => {
    setIsMenuOpened(true);
  };
  const handleMenuClose = () => {
    setIsMenuOpened(false);
  };
  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };
  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleAddImage = (url: string, index: number) => {
    if (data?.options) {
      setCurrentURL(url);
      const options = [...data.options];
      options[index].image = url;
      onAddOptions?.(options);
    }
  };

  const handleReorderOptions = (dragInfo) => {
    const { source, destination } = dragInfo;
    if (data?.options) {
      const currentOptions = [...data.options];
      const temp = currentOptions[destination.index];
      currentOptions[destination.index] = currentOptions[source.index];
      currentOptions[source.index] = temp;

      onReorderFormFieldOptions?.(currentOptions);
    }
  };

  const handleAddOption = () => {
    if (data.options) {
      const options = [...data.options, { label: '', value: '', optionId: uuid() }];
      onAddOptions?.(options);
    }
  };

  const handleAddOptionData = (value: string, index: number) => {
    if (data.options) {
      const options = [...data.options];
      options[index].label = value;
      options[index].value = `${value}-${index}`;
      onAddOptions?.(options);
    }
  };

  const handleDeleteOption = (index: number) => {
    if (data?.options) {
      const options = [...data.options];
      onDeleteOption?.(options[index]);
    }
  };

  const handleEditImage = (index: number, imageUrl?: string) => {
    if (data?.options) {
      const options = [...data.options];
      if (options[index].image && !imageUrl) delete options[index].image;
      else {
        const options = [...data.options];
        options[index].image = imageUrl;
      }
      onAddOptions?.(options);
    }
  };

  const getRatingLabel = (ratingStyle: string) => {
    const data = styleoption.find((item) => item.value === ratingStyle);
    return data?.label;
  };

  const handleScrollToEnd = () => {
    const formContainer = document?.getElementById('form-container');
    if (formContainer) {
      formContainer.scrollTo({
        top: formContainer.scrollHeight,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const assignedDates = useMemo(() => {
    if (data.dateRange?.length)
      return data.dateRange
        .map((date) => (date && typeof date === 'string' ? parseISO(date) : date))
        .filter((date) => !!date);
    return [];
  }, [data]);

  return (
    <div className="flex text-gray-500 items-start">
      <div className="flex mt-12 items-center">
        <Popover
          closeOnBlur
          onClose={handleMenuClose}
          onOpen={handleMenuOpen}
          isOpen={isMenuOpened}
          placement="left-start"
          modifiers={[{ name: 'eventListeners', options: { scroll: isMenuOpened } }]}
        >
          {({ isOpen }) => (
            <Fragment>
              <button id="clickAdd" onClick={onAddNewField} className="text-gray-500">
                <AddIcon />
              </button>
              <button
                className="w-full text-left flex justify-between items-center text-gray-500"
                {...dragHandleProps}
              >
                <span
                  className={cn({
                    'px-8 py-1 rounded': true,
                  })}
                >
                  <DragIcon size={8} />
                </span>
              </button>
            </Fragment>
          )}
        </Popover>
      </div>
      <div style={{ width: 'calc(100% - 54px)' }}>
        <div
          className={cn(
            'font-regular text-xs text-gray-500 mb-30 border border-gray-200 rounded-lg p-16 bg-white'
          )}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-16">
              <Tippy
                disabled={!isFormToTaskSelectedField}
                content={
                  'This question is being used as the selected question when saving forms to tasks. Either unlink the file or change the selected question to delete this field.'
                }
                className="w-auto h-auto bg-gray-999 tippy text-xss"
                placement="top"
              >
                <span>
                  <SelectOption
                    disabled={isFormToTaskSelectedField}
                    value={options.find((option) => option.value === data.responseType)}
                    options={options}
                    onChange={(option) => onTypeChange(option.value)}
                  />
                </span>
              </Tippy>
              <div className="text-base text-black">Question #{index + 1}</div>
            </div>
            <div>
              <div className="flex ">
                <Tippy
                  disabled={!timerEnabled}
                  content={
                    'Timed forms cannot have fields marked as “required”.Disable form timer to add required fields'
                  }
                  className="w-auto h-auto bg-gray-999 tippy text-xss"
                  placement="top"
                >
                  <span>
                    <span className="mr-6 ml-4 mt-4 text-blue-400">
                      <Switch
                        checked={!!data.isRequired && !timerEnabled}
                        onChange={() => onRequiredChange(!data.isRequired)}
                        disabled={timerEnabled}
                        className={timerEnabled ? 'opacity-75' : ''}
                      />
                    </span>
                    <span className="text-sm">Required</span>
                  </span>
                </Tippy>
                <Popover
                  closeOnBlur
                  onClose={handleSettingsClose}
                  onOpen={handleSettingsOpen}
                  isOpen={settingsOpen}
                  placement="bottom-end"
                >
                  <PopoverTrigger>
                    <button className="ml-20 text-base bg-gray-100 px-4 rounded">
                      <SettingsIcon />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-6 bg-white shadow-lg rounded-md max-h-304 min-w-300 overflow-visible">
                    <div className="p-10">
                      {data.responseType &&
                        ['short-text', 'paragraph', 'dropdown', 'email', 'phone'].includes(
                          data.responseType
                        ) && (
                          <>
                            <div>
                              <p className="mb-8">
                                Placeholder text
                                <Tippy
                                  content={
                                    <TooltipContent
                                      description={tooltip.placeholder.description}
                                      title={tooltip.placeholder.title}
                                    />
                                  }
                                  interactive
                                  className="w-auto h-auto bg-gray-999 tippy text-xss"
                                  placement="bottom"
                                >
                                  <span className="align-middle ml-8 " role="button">
                                    <InfoIcon className="text-gray-500" />
                                  </span>
                                </Tippy>
                              </p>
                              <input
                                name="placeholder"
                                value={data.placeholder}
                                onChange={(e) => onPlaceholderChange(e.target.value)}
                                className={cn(
                                  'border border-gray-200 outline-none rounded-md text-sm  pl-12 py-13 text-gray-600 h-48 bg-gray-100 w-full'
                                )}
                              />
                            </div>
                          </>
                        )}

                      {data.responseType === 'linear-scale' && (
                        <>
                          <div className="w-full flex mb-16">
                            <div className="w-full mr-24">
                              <p className="text-sm text-gray-600 mb-8">Lowest scale range</p>
                              <SelectOption
                                value={
                                  data.lowerRange
                                    ? { label: data.lowerRange, value: data.lowerRange }
                                    : { label: 0, value: 0 }
                                }
                                options={getScaleOptions(1, 10)}
                                onChange={(option) => {
                                  onChangeRange?.(option.value, 'lowest');
                                }}
                                placeholder={'Select Range'}
                              />
                            </div>
                            <div className="w-full">
                              <p className="text-sm text-gray-600 mb-8">Lowest scale label</p>

                              <input
                                name="description"
                                value={data.lowerRangeLabel}
                                onChange={(e) => onChangeRangeLabels?.(e.target.value, 'lowest')}
                                placeholder="Type label"
                                className={cn(
                                  'border  border-gray-200 outline-none rounded-md text-sm pl-12 py-13  text-gray-900 h-48 bg-gray-100 w-full'
                                )}
                              />
                            </div>
                          </div>
                          <div className="w-full flex">
                            <div className="w-full mr-24">
                              <p className="text-sm text-gray-600 mb-8">Highest scale range</p>
                              <SelectOption
                                value={
                                  data.higherRange
                                    ? { label: data.higherRange, value: data.higherRange }
                                    : { label: 10, value: 10 }
                                }
                                options={getScaleOptions(1, 10)}
                                onChange={(option) => {
                                  onChangeRange?.(option.value, 'highest');
                                }}
                                placeholder={'Select Range'}
                              />
                            </div>
                            <div className="w-full">
                              <p className="text-sm text-gray-600 mb-8">Highest scale label</p>

                              <input
                                name="description"
                                value={data.higherRangeLabel}
                                onChange={(e) => onChangeRangeLabels?.(e.target.value, 'highest')}
                                placeholder="Type label"
                                className={cn(
                                  'border  border-gray-200 outline-none rounded-md text-sm pl-12 py-13  text-gray-900 h-48 bg-gray-100 w-full'
                                )}
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {data.responseType === 'star-rating' && (
                        <>
                          <div className="w-full">
                            <div className="w-full mb-16">
                              <p className="text-sm text-gray-600 mb-8">Maximum rating number</p>
                              <SelectOption
                                label={'Maximum rating number'}
                                value={
                                  data.higherRange
                                    ? { label: data.higherRange, value: data.higherRange }
                                    : { label: 5, value: 5 }
                                }
                                placeholder={'Select option'}
                                options={getScaleOptions(1, 10)}
                                onChange={(option) => {
                                  onChangeRange?.(option.value, 'highest');
                                }}
                              />
                            </div>
                            <div className="w-full">
                              <p className="text-sm text-gray-600 mb-8">Rating style</p>
                              <SelectOption
                                label="Rating style"
                                value={
                                  data.ratingStyle
                                    ? {
                                        label: getRatingLabel(data.ratingStyle),
                                        value: data.ratingStyle,
                                      }
                                    : {
                                        label: (
                                          <div>
                                            <StarFilledIcon className="text-blue-700 text-base block" />{' '}
                                            Star
                                          </div>
                                        ),
                                        value: 'star',
                                      }
                                }
                                placeholder={'Select style'}
                                options={styleoption}
                                onChange={(option) => {
                                  onChangeRatingStyle?.(option.value);
                                }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {data.responseType &&
                        ['radio-box', 'checkbox', 'dropdown'].includes(data?.responseType) && (
                          <div
                            className={`flex justify-between ${
                              data?.responseType === 'dropdown' ? 'mt-24' : ''
                            }`}
                          >
                            <span className="text-sm">Randomize option order</span>
                            <span className="ml-12 mt-4 text-blue-400">
                              <Switch
                                checked={!!data.randomiseOptions}
                                onChange={() => onRandomizeOptions?.(!data.randomiseOptions)}
                              />
                            </span>
                          </div>
                        )}
                      {data.responseType && ['date-picker'].includes(data?.responseType) && (
                        <div>
                          <p className="mb-8 text-sm">Define a period</p>
                          <div className="flex flex-wrap gap-12">
                            <DateRangePicker
                              placement="auto-end"
                              value={
                                assignedDates.length > 0
                                  ? (assignedDates as [Date] | [Date, Date])
                                  : null
                              }
                              onChange={(dateRange) => {
                                onChangeDateRange?.(dateRange);
                              }}
                              inputClassName="date-picker bg-gray-100 p-12 w-332 rounded-sm h-48 border border-gray-200 text-left text-base"
                            />
                          </div>
                        </div>
                      )}
                      {data.responseType === 'upload' && (
                        <>
                          <div className="w-full">
                            <div className="w-full mb-16">
                              <p className="text-sm text-gray-600 mb-8">
                                Number of files per response
                              </p>
                              <SelectOption
                                label={'Number of files per response'}
                                value={
                                  data.maxFilesCount
                                    ? { label: data.maxFilesCount, value: data.maxFilesCount }
                                    : { label: 1, value: 1 }
                                }
                                placeholder={'Select option'}
                                options={Array.from(Array(10).keys()).map((x, i) => ({
                                  label: i + 1,
                                  value: i + 1,
                                }))}
                                onChange={(option) => {
                                  onChangeMaxFiles(option.value);
                                }}
                              />
                            </div>
                            <div className="w-full mb-16">
                              <p className="text-sm text-gray-600 mb-8">Maximum File Size</p>
                              <label className="relative text-gray-400 focus-within:text-gray-600 block">
                                <span
                                  className={cn(
                                    'pointer-events-none absolute right-0 px-20 transform -translate-y-1/2 text-sm py-6 border-l',
                                    !!data.maxFileSize &&
                                      data.maxFileSize > 1000 /** error check here */ &&
                                      'mb-10'
                                  )}
                                  style={{ top: '50%' }}
                                >
                                  MB
                                </span>
                                <input
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    onChangeMaxFileSize(parseInt(val));
                                  }}
                                  defaultValue={data.maxFileSize || 2} /** MBs */
                                  type="email"
                                  name="email"
                                  id="email"
                                  placeholder="Enter a value"
                                  className={cn(
                                    'border border-gray-200 outline-none rounded-md text-sm pl-12 py-6 w-full text-gray-900 h-48 bg-gray-100 w-73p pr-14'
                                  )}
                                />
                              </label>
                              {!!data.maxFileSize &&
                                data.maxFileSize > 1000 /** error check here */ && (
                                  <label className="text-red-800">
                                    This value can’t be more than 1000 MB (1 GB)
                                  </label>
                                )}
                              {!isNil(data.maxFileSize) &&
                                data.maxFileSize <= 0 /** error check here */ && (
                                  <label className="text-red-800">
                                    This value needs to be greater than 0
                                  </label>
                                )}
                            </div>
                          </div>
                          <div>
                            <span className="text-sm text-gray-600">File Types</span>
                            <div className="mt-8">
                              <SelectOption
                                className={cn(
                                  data.fileTypes && !data.fileTypes.length && 'border-red-800'
                                )}
                                placeholder="Any (*)"
                                isMulti
                                value={
                                  data.fileTypes?.map((op) => {
                                    const type = FileUploadTypes[op];
                                    return { value: type?.key, label: type?.label };
                                  }) || []
                                }
                                onChangeMulti={(res) => {
                                  const values = res.map((r) => r.value);
                                  onChangeFileTypes(values);
                                }}
                                options={Object.values(FileUploadTypes).map((op) => ({
                                  label: op.label,
                                  value: op.key,
                                }))}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                <Tippy
                  content={
                    'This question is being used as the selected question when saving forms to tasks. Either unlink the file or change the selected question to delete this field.'
                  }
                  className="w-auto h-auto bg-gray-999 tippy text-xss"
                  placement="top"
                  disabled={!isFormToTaskSelectedField}
                >
                  <span className="ml-20 text-base">
                    <button
                      className={cn(isFormToTaskSelectedField && 'opacity-75')}
                      onClick={onDeleteField}
                      disabled={isFormToTaskSelectedField}
                    >
                      <DeleteIcon />
                    </button>
                  </span>
                </Tippy>
              </div>
            </div>
          </div>
          <div className="w-full border-t border-gray-200 mb-16 mt-8" />
          <div>
            <input
              name="title"
              value={data.title}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder={data.responseType === 'upload' ? 'Type your question here' : 'Title'}
              className={cn(
                'border border-gray-200 outline-none rounded-md text-sm pl-12 py-6 text-gray-900 h-48 bg-white mb-16 w-full'
              )}
            />
            <input
              name="description"
              value={data.description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={
                data.responseType === 'upload' ? 'Type optional supporting text ...' : 'Description'
              }
              className={cn(
                'border  border-gray-200 outline-none rounded-md text-sm pl-12 py-6  text-gray-900 h-48 bg-gray-100 w-full mb-16'
              )}
            />
            {data.responseType === 'upload' && (
              <>
                <FileUpload
                  className={cn('mb-16')}
                  translations={{
                    attachments_drop_label: 'Attach files',
                    attachments_label: 'Attach files ',
                  }}
                  acceptTypes={['any']}
                  disabled
                  onDrop={() => null}
                />
              </>
            )}
            {data?.image && data?._id && data?.showImage && (
              <div
                className="relative"
                onMouseLeave={() => setViewImage('')}
                onMouseEnter={() => setViewImage(data?._id)}
              >
                <img src={data?.image} className="w-full object-center object-cover h-330" />
                {/* {isLoading && (
                      <div className="absolute right-0 left-0 mx-auto top-0 bottom-0">
                        <LoaderXs />
                      </div>
                    )} */}
                {/* {isUploadImageOpen && (
                      <UploadImageWithoutModal
                        onSubmit={(url) => {
                          setIsUploadImageOpen(false);
                          onAddImage?.(url);
                        }}
                         onLoadingImage={(isLoading) => setIsLoading(isLoading)}
                        isOpen={isUploadImageOpen}
                        onCancelFileUpload={() => {
                    
                          setIsUploadImageOpen(false);
                        }}
                      />
                    )} */}

                {viewImage === data?._id && (
                  <div className="absolute right-2 top-10 py-6 px-6 text-gray-600 text-xs rounded-sm bg-gray-100 flex">
                    {/* <button
                          onClick={() => {
                            setIsUploadImageOpen(true);
                          }}
                        >
                          Replace
                        </button>
                        <span className="px-6">|</span> */}
                    <button onClick={() => onAddImage?.(undefined)}>Delete</button>
                  </div>
                )}
              </div>
            )}

            {!data.showImage && (
              <div className="mt-16">
                <UploadImage
                  button={
                    <Button iconLeft={<AddIcon />} status="ghost">
                      Add image
                    </Button>
                  }
                  onSubmit={(url) => {
                    onAddImage?.(url);
                    // handleScrollToEnd();
                  }}
                />
              </div>
            )}

            {(data.responseType === 'radio-box' ||
              data.responseType === 'checkbox' ||
              data.responseType === 'dropdown') && (
              <div>
                <p className="mt-24 mb-8 text-sm text-gray-500">Options</p>
                <div className="relative">
                  <DragDropContext onDragEnd={handleReorderOptions}>
                    <Droppable droppableId="form-options">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            position: 'relative',
                          }}
                        >
                          {data.options?.map((item, index) => (
                            <Draggable
                              draggableId={`${index}-${item.label}`}
                              key={index}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="mb-16 drag-options"
                                  onMouseEnter={() => setHoveredOptionIndex(index)}
                                  onMouseLeave={() => setHoveredOptionIndex(-1)}
                                >
                                  <div className="flex my-8 w-59p">
                                    <div className="flex mr-12 items-center	min-w-8 text-gray-400">
                                      {hoveredOptionIndex === index && (
                                        <div {...provided.dragHandleProps}>
                                          <DragIcon
                                            className="text-gray-500 "
                                            width="8px"
                                            height="14px"
                                          />
                                        </div>
                                      )}
                                    </div>

                                    <div className="border border-gray-200 w-3/4  flex p-10 rounded-md text-gray-999 h-48  bg-gray-100">
                                      <span className="text-xs items-center flex flex-grow">
                                        {data?.responseType === 'radio-box' ? (
                                          <div className="h-16 w-16 border-2 mr-4 bg-transparent rounded-full" />
                                        ) : data.responseType === 'checkbox' ? (
                                          <SquareIcon size={20} />
                                        ) : (
                                          <span className="text-xs leading-5 font-medium text-gray-900 mr-6">
                                            {index + 1}.
                                          </span>
                                        )}
                                      </span>

                                      <input
                                        className="w-full flex-grow bg-gray-100"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleAddOption();
                                            e.preventDefault();
                                          }
                                        }}
                                        autoFocus
                                        value={item.label}
                                        onChange={(e) => handleAddOptionData(e.target.value, index)}
                                      />
                                    </div>

                                    {data?.responseType !== 'dropdown' && (
                                      <UploadImage
                                        button={<ImageIcon className="text-xl ml-8" />}
                                        onSubmit={(url) => handleAddImage(url, index)}
                                      />
                                    )}

                                    <button className="" onClick={() => handleDeleteOption(index)}>
                                      <CloseIcon className="text-xs mt-2 ml-18" />
                                    </button>
                                  </div>

                                  {item.image && (
                                    <div
                                      className="mb-10"
                                      onMouseEnter={() => setImageEditOption(item.optionId || '')}
                                      onMouseLeave={() => setImageEditOption(item.optionId || '')}
                                    >
                                      <div className="mx-auto relative">
                                        <img
                                          className="object-center object-cover rounded-sm ml-20 max-w-440 max-h-221"
                                          src={item.image}
                                        />
                                      </div>
                                      {/* {item.optionId === imageEditOption && (
                                            <div className="absolute right-0 top-10 py-6 px-6 text-gray-600 text-xs rounded-sm bg-gray-100 flex">
                                              <button onClick={() => handleEditImage(index)}>
                                                Delete
                                              </button>
                                            </div>
                                          )} */}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
                <Button className="bg-blue-200 text-lg px-16 rounded-md " onClick={handleAddOption}>
                  Add option
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
const TooltipContent = ({ title, description }: { title: string; description: string }) => {
  return (
    <div className="p-16">
      <p className="mb-8 font-medium text-base">{title}</p>
      <p className="mb-18 text-sm">{description}</p>
    </div>
  );
};
export const Item = forwardRef<HTMLButtonElement, ItemProps>(
  // eslint-disable-next-line react/prop-types
  ({ isRightArrow, label, leftIcon: LeftIcon, ...otherProps }, ref) => {
    return (
      <button
        ref={ref}
        className="px-8 w-full text-left py-7 rounded-sm min-w-btn-lg hover:bg-gray-100 cursor-pointer align-middle justify-between flex items-center font-regular"
        {...otherProps}
      >
        <span className="flex items-center">
          {<LeftIcon className="mr-8 text-lg text-gray-500" />}
          <span className="text-sm">{label}</span>
        </span>
        {isRightArrow ? (
          <span
            style={{
              transform: 'rotate(-90deg)',
              transition: 'all 0.5s',
            }}
          >
            <ChevronDownIcon className="text-gray-200" />
          </span>
        ) : null}
      </button>
    );
  }
);

Item.displayName = 'Item';

export interface ItemProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  leftIcon: ({ className }: { className: string }) => JSX.Element;
  label?: string;
  isRightArrow?: boolean;
}

const tooltip = {
  placeholder: {
    title: 'Placeholder text',
    description: `This is what the responders will 
see when a blank cell comes up. 
You can add an example here. 
Think names, expected values, etc. 
Placeholder text won’t be submitted
with the form if left blank`,
  },
  default: {
    title: 'Default value',
    description: `You can assign a default field 
value here. This value will be 
submitted with the form unless 
edited`,
  },
};

export const getScaleOptions = (min: number, max: number) => {
  const options: any[] = [];
  for (let i = min; i <= max; i++) {
    options.push({
      value: i,
      label: i,
    });
  }
  return options;
};
